import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import PropTypes from 'prop-types';
import styles from './dropdown.module.scss';
import { useKeyPress, constants } from '../shared/js';
import Grid from '../Grid';
import Option from './Option';
import { Icons, HTML } from '../';
const { UP_KEY, DOWN_KEY } = constants;

const DropdownOptions = ({
  options,
  fieldValue,
  onClick,
  isOpen,
  id,
  fullScreen,
  label,
  onClose,
  searchResults,
  searchedIndex,
  className, // eslint-disable-line react/prop-types
  optionClassName, // eslint-disable-line react/prop-types
}) => {
  const [activeOption, setActiveOption] = useState(null);
  const upPressed = useKeyPress(UP_KEY);
  const downPressed = useKeyPress(DOWN_KEY);
  const optionsRef = useRef();

  useEffect(() => {
    if (!isOpen) return;
    upPressed && navigateOptions('up');
    downPressed && navigateOptions('down');
  }, [upPressed, downPressed, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchedIndex < 0) return;
    setActiveOption(searchedIndex);
  }, [searchedIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateOptions = (direction) => {
    if (activeOption === null || typeof activeOption === 'undefined') {
      setActiveOption(0);
    } else if (direction === 'up' && activeOption > 0) {
      setActiveOption(activeOption - 1);
    } else if (direction === 'up') {
      setActiveOption(options.length - 1);
    } else if (direction === 'down' && activeOption >= options.length - 1) {
      setActiveOption(0);
    } else if (direction === 'down') {
      setActiveOption(activeOption + 1);
    }
  };

  useKeyPress(UP_KEY, navigateOptions);
  useKeyPress(DOWN_KEY, navigateOptions);

  useEffect(() => {
    if (!optionsRef?.current || !fullScreen) return;
    if (isOpen) document.body.classList.add(styles.scrollLockMobile);
    else document.body.classList.remove(styles.scrollLockMobile);
  }, [isOpen, fullScreen]);

  return (
    <ul
      className={cn(styles.options, className, {
        [styles.open]: isOpen,
        [styles.fullScreen]: fullScreen,
      })}
    >
      {fullScreen && (
        <HTML className={styles.mobileTitleBar} tabletOnly>
          <Grid.Row>
            <Grid.Column col8>
              <label className={styles.mobileTitle}>{label}</label>
            </Grid.Column>
            <Grid.Column col4>
              <button
                className={styles.mobileClose}
                type="button"
                onClick={onClose}
              >
                <FormattedMessage id="buttons.close" defaultMessage="Close" />
                <div className={styles.iconWrapper}>
                  <Icons.Close />
                  <div className={styles.underlineElement} />
                </div>
              </button>
            </Grid.Column>
          </Grid.Row>
        </HTML>
      )}
      <div className={styles.optionsContainer} ref={optionsRef} tabIndex={-1}>
        {options.map((option, index) => (
          <Option
            searchResult={searchResults}
            key={index}
            isActive={activeOption === index}
            selected={
              (!!option.value || option.value === '') &&
              option.value === fieldValue
            }
            onClick={() => onClick(option)}
            id={id && `${id}${index}`}
            className={optionClassName}
          >
            {option.label}
          </Option>
        ))}
      </div>
    </ul>
  );
};

DropdownOptions.propTypes = {
  id: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object),
  fieldValue: PropTypes.any,
  onClick: PropTypes.func,
  isOpen: PropTypes.bool,
  fullScreen: PropTypes.bool,
  label: PropTypes.any,
  onClose: PropTypes.func,
  searchResults: PropTypes.bool,
  searchedIndex: PropTypes.number,
};

DropdownOptions.defaultProps = {
  id: undefined,
  options: [],
  fieldValue: '',
  onClick: undefined,
  isOpen: false,
  fullScreen: false,
  label: undefined,
  onClose: () => {},
  searchResults: false,
};

export default DropdownOptions;
